import { Modal, IconButton, TextField, Button, MenuItem } from "@mui/material"
import { z } from "zod"
import ClearIcon from "@mui/icons-material/Clear"
import { useAddModel } from "@/hooks/useAddModel"
import useLocations from "@/hooks/useLocations"
import { useEffect } from "react"

const DeliveryFeeSchema = z.object({
  location_id: z.number({ invalid_type_error: "City is required" }),
  fee: z.coerce.number().min(0, "Fee must be 0 or more"),
})

const AddDeliveryFeeModal = ({ open, onClose }) => {
  const {
    branchId,
    branchSelector,
    errors,
    openDialog,
    isSubmitting,
    selectedRow,
    control,
    Controller,
    register,
    handleSubmit,
    onSubmit,
    handleReset,
    defaultOnClose,
    reset,
  } = useAddModel("delivery-fee", DeliveryFeeSchema, "delivery-fee")

  const { locations } = useLocations()
  const cities = locations.filter(
    (loc) => loc.type === "city" && (!branchId || loc.branch_id === branchId)
  )

  useEffect(() => {
    if (selectedRow) {
      reset({
        location_id: selectedRow.location_id,
        fee: selectedRow.fee,
      })
    } else {
      reset({ location_id: null, fee: "" })
    }
  }, [selectedRow, reset])

  return (
    <Modal open={open ? open : openDialog} onClose={defaultOnClose}>
      <div className="w-[95vw] sm:w-[90vw] md:w-[500px] mx-auto mt-24 bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="flex items-center justify-between bg-[#f6f7f9] px-4 py-3 rounded-t-lg">
          <h2 className="font-semibold text-black text-[16px] md:text-[18px]">
            {selectedRow ? "Update Delivery Fee" : "Add New Delivery Fee"}
          </h2>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-4">
          {branchSelector && <div className="mb-4">{branchSelector}</div>}
          {/* City Field */}
          <div className="mb-4">
            <Controller
              name="location_id"
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="City"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === "" ? null : Number(e.target.value)
                    )
                  }
                  error={!!errors.location_id}
                  helperText={errors.location_id?.message}
                >
                  {cities.length === 0 && (
                    <MenuItem value="" disabled>
                      No city found — add locations first
                    </MenuItem>
                  )}
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                      {city.parent_name ? ` (${city.parent_name})` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </div>

          {/* Fee Field */}
          <div className="mb-4">
            <TextField
              fullWidth
              type="number"
              label="Fee (Rs.)"
              inputProps={{ min: 0, step: "0.01" }}
              error={!!errors.fee}
              helperText={errors.fee?.message}
              {...register("fee")}
            />
          </div>

          {/* Button Row */}
          <div className="flex justify-end items-center gap-2 mt-2 flex-wrap">
            <Button
              onClick={handleReset}
              variant="text"
              className="capitalize rounded-md font-medium text-[13px] py-[11px] px-[30px] w-full sm:w-auto"
            >
              Reset
            </Button>

            <Button
              onClick={onClose ? onClose : defaultOnClose}
              variant="outlined"
              color="error"
              className="capitalize rounded-md font-medium text-[13px] py-[11px] px-[30px] w-full sm:w-auto"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              className="capitalize rounded-md font-medium text-[13px] py-[11px] px-[30px] text-white w-full sm:w-auto"
            >
              {selectedRow ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default AddDeliveryFeeModal
